import { z } from "zod";

import siteJson from "../../content/site.json";

// The production domain lives in exactly one file — content/site.json.
// Canonical tags, the sitemap, robots.txt and the JSON-LD emitters all
// read it from here, so moving domains is a one-line data change.
const siteConfigSchema = z.object({
  site_url: z.string().url(),
  site_name: z.string().min(1),
  legal_name: z.string().min(1),
  tagline: z.string().min(1),
  logo_path: z.string().startsWith("/"),
  og_image_path: z.string().startsWith("/"),
  contact_email: z.string().email(),
  same_as: z.array(z.string().url()),
});

export type SiteConfig = z.infer<typeof siteConfigSchema>;

function loadSiteConfig(): SiteConfig {
  const parsed = siteConfigSchema.safeParse(siteJson);
  if (!parsed.success) {
    throw new Error(`content/site.json is invalid: ${parsed.error.message}`);
  }
  // A trailing slash here would produce "https://revetra.net//pricing"
  // in every canonical tag and sitemap entry.
  const siteUrl = parsed.data.site_url.replace(/\/+$/, "");
  return { ...parsed.data, site_url: siteUrl };
}

export const site = loadSiteConfig();

/** Absolute URL for a site-root-relative path, for canonicals and JSON-LD. */
export function absoluteUrl(path: string): string {
  if (!path.startsWith("/")) {
    throw new Error(`Expected a root-relative path, got "${path}"`);
  }
  return `${site.site_url}${path}`;
}
